import React, { useState, useRef, useEffect } from 'react';
import 'react-quill/dist/quill.snow.css';
import FileManagerService from '../../services/fileManagerService';
import Quill from 'quill';

// Step 1: Basic Quill setup without MathJax - checking for errors
console.log('Step 1: Initializing Quill editor components');

// Custom Quill icon setup
if (typeof window !== 'undefined') {
  try {
    const icons = Quill.import('ui/icons');
    icons['code'] = '<svg viewBox="0 0 18 18"><path class="ql-fill" d="M2,12.1c-0.3-0.3-0.3-0.8,0-1.1l1.6-1.6L2,7.9c-0.3-0.3-0.3-0.8,0-1.1s0.8-0.3,1.1,0l2.1,2.1c0.3,0.3,0.3,0.8,0,1.1l-2.1,2.1C2.8,12.4,2.3,12.4,2,12.1z M9.4,13.5h-1c-0.2,0-0.4-0.2-0.4-0.4s0.2-0.4,0.4-0.4h1c0.2,0,0.4,0.2,0.4,0.4S9.6,13.5,9.4,13.5z M14.9,12.1l-2.1-2.1c-0.3-0.3-0.3-0.8,0-1.1l2.1-2.1c0.3-0.3,0.8-0.3,1.1,0s0.3,0.8,0,1.1L14.4,9.5l1.6,1.6c0.3,0.3,0.3,0.8,0,1.1S15.2,12.4,14.9,12.1z"/></svg>';
    console.log('Step 1: Quill icons registered successfully');
  } catch (e) {
    console.error('Step 1 Error: Failed to register Quill icons:', e);
  }
}

// Step 2: Localized MathJax Preview Component - isolated from main component
const MathJaxPreview = ({ value }) => {
  const [mathJaxReady, setMathJaxReady] = useState(false);
  const previewRef = useRef(null);
  
  // Load MathJax only for this preview component
  useEffect(() => {
    console.log('MathJax Preview: Starting localized MathJax integration');
    
    // Skip if already initialized
    if (mathJaxReady || window.MathJax) {
      console.log('MathJax Preview: MathJax already initialized');
      if (window.MathJax) {
        setMathJaxReady(true);
      }
      return;
    }
    
    // Clean up any existing MathJax
    const existingScript = document.getElementById('mathjax-preview-script');
    if (existingScript) {
      console.log('MathJax Preview: Removing existing MathJax script');
      existingScript.remove();
    }
    
    // Set up MathJax configuration
    console.log('MathJax Preview: Setting up MathJax configuration');
    window.MathJax = {
      tex: {
        inlineMath: [['$', '$'], ['\\(', '\\)']],
        displayMath: [['$$', '$$'], ['\\[', '\\]']],
        processEscapes: true,
        processEnvironments: true
      },
      options: {
        enableMenu: false,
        skipHtmlTags: ['script', 'noscript', 'style', 'textarea', 'pre', 'code']
      },
      startup: {
        ready: () => {
          console.log('MathJax Preview: MathJax startup ready');
          try {
            if (window.MathJax && window.MathJax.startup) {
              window.MathJax.startup.defaultReady();
              setMathJaxReady(true);
              console.log('MathJax Preview: MathJax initialization completed successfully');
            }
          } catch (error) {
            console.error('MathJax Preview Error: MathJax startup failed:', error);
          }
        }
      }
    };
    
    // Load MathJax script
    console.log('MathJax Preview: Loading MathJax script');
    const script = document.createElement('script');
    script.id = 'mathjax-preview-script';
    script.src = 'https://cdn.jsdelivr.net/npm/mathjax@3.2.2/es5/tex-mml-chtml.js';
    script.async = true;
    
    script.onload = () => {
      console.log('MathJax Preview: MathJax script loaded successfully');
    };
    
    script.onerror = (error) => {
      console.error('MathJax Preview Error: Failed to load MathJax script:', error);
    };
    
    document.head.appendChild(script);
    
    // Cleanup function
    return () => {
      console.log('MathJax Preview: Cleaning up MathJax');
      const scriptToRemove = document.getElementById('mathjax-preview-script');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);
  
  // Typeset math when value changes
  useEffect(() => {
    if (!previewRef.current || !value || !mathJaxReady) {
      console.log('MathJax Preview: Skipping typesetting - not ready or no value');
      return;
    }
    
    const typesetMath = async () => {
      console.log('MathJax Preview: Attempting to typeset math');
      
      try {
        if (window.MathJax && typeof window.MathJax.typesetPromise === 'function') {
          console.log('MathJax Preview: Using MathJax.typesetPromise');
          await window.MathJax.typesetPromise([previewRef.current]);
          console.log('MathJax Preview: Typesetting completed successfully');
        } else if (window.MathJax && typeof window.MathJax.typeset === 'function') {
          console.log('MathJax Preview: Using MathJax.typeset');
          window.MathJax.typeset([previewRef.current]);
          console.log('MathJax Preview: Typesetting completed successfully');
        } else {
          console.log('MathJax Preview: No suitable typesetting method found');
        }
      } catch (error) {
        console.error('MathJax Preview Error: Typesetting failed:', error);
      }
    };
    
    const timer = setTimeout(() => {
      typesetMath();
    }, 100);
    
    return () => {
      clearTimeout(timer);
    };
  }, [value, mathJaxReady]);
  
  const handleManualRefresh = async () => {
    console.log('MathJax Preview: Manual refresh triggered');
    if (window.MathJax && previewRef.current && mathJaxReady) {
      try {
        if (typeof window.MathJax.typesetPromise === 'function') {
          console.log('MathJax Preview: Using typesetPromise for manual refresh');
          await window.MathJax.typesetPromise([previewRef.current]);
          console.log('MathJax Preview: Manual refresh completed successfully');
        } else {
          console.log('MathJax Preview: typesetPromise not available for manual refresh');
        }
      } catch (error) {
        console.error('MathJax Preview Error: Manual refresh failed:', error);
      }
    } else {
      console.warn('MathJax Preview: MathJax not ready for manual refresh');
    }
  };
  
  return (
    <div className="mt-6 border-t pt-4">
      <h3 className="text-lg font-semibold mb-3">
        MathJax Preview (Localized Integration)
        <span className={`ml-2 text-sm ${
          mathJaxReady ? 'text-green-600' : 'text-red-600'
        }`}>
          {mathJaxReady ? '✓ Ready' : '⏳ Loading...'}
        </span>
      </h3>
      <div 
        ref={previewRef} 
        className="p-4 border rounded-md bg-gray-50 min-h-[100px]"
        dangerouslySetInnerHTML={{ __html: value }}
      />
      <div className="mt-2 flex items-center space-x-2">
        <button 
          onClick={handleManualRefresh}
          className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200"
          disabled={!mathJaxReady}
        >
          Refresh Math
        </button>
        <span className="text-xs text-gray-500">
          Try typing: $x^2 + y^2 = z^2$ or click the ∑ button above
        </span>
      </div>
    </div>
  );
};

// Step 3: Clean component setup - checking for basic state management
console.log('Step 3: Setting up QuillEditor component');

const QuillEditor = () => {
  // Basic state management
  const [value, setValue] = useState('');
  const [showSource, setShowSource] = useState(false);
  const [htmlValue, setHtmlValue] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  
  // Refs for DOM elements
  const quillRef = useRef(null); // Quill instance
  const editorRef = useRef(null); // Div for Quill
  
  const fileManager = FileManagerService;
  
  console.log('Step 3: Component state initialized successfully (MathJax removed from main component)');

  // Step 3: MathJax integration removed from global scope - now handled locally in preview

  // Wrap uploadImage in useCallback to prevent recreation on each render
  const uploadImage = React.useCallback(async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      console.error('Please select a valid image file');
      return;
    }

    setIsUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('fmFile', file);
      
      const response = await fileManager.uploadFile(formData, {
        name: file.name,
        size: file.size,
        type: file.type,
      });
      
      if (response && response.url) {
        const quill = quillRef.current;
        if (quill) {
          const range = quill.getSelection(true) || { index: quill.getLength() };
          quill.insertEmbed(range.index, 'image', response.url);
          quill.setSelection(range.index + 1, 0); // Move cursor after the image
        }
      } else {
        console.error('Upload response did not contain a URL:', response);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
    } finally {
      setIsUploading(false);
    }
  }, [fileManager, quillRef]);

  // Image handler for toolbar - used in quill modules
  const imageHandler = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = () => {
      if (input.files && input.files.length > 0) {
        const file = input.files[0];
        uploadImage(file);
      }
    };
  };

  const toggleSourceCode = () => {
    if (!showSource && quillRef.current) {
      // Switching to source view - save current content
      const html = quillRef.current.root.innerHTML;
      setHtmlValue(html);
    } else if (showSource && quillRef.current) {
      // We'll handle the content update in the useEffect that runs after showSource changes
    }
    setShowSource(!showSource);
  };
  
  // Handle drag and drop events
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find(file => file.type.startsWith('image/'));
    
    if (imageFile) {
      uploadImage(imageFile);
    }
  };
  
  // Handle paste events for images
  // Wrap handlePaste in useCallback to prevent recreation on each render
  const handlePaste = React.useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault();
        const blob = item.getAsFile();
        uploadImage(blob);
        break;
      }
    }
  }, [uploadImage]);
  
  // Custom handler for the source button in toolbar - used in toolbar handlers
  const handleSourceClick = () => {
    toggleSourceCode();
  };

  // Paste handler is now set up in the Quill initialization

  // Drag and drop is handled at the container level now

  // Modules are now defined directly in the Quill initialization

  // Formats supported by the editor - used in Quill initialization
  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'image',
  ];

  // Load MathJax once with proper configuration to avoid conflicts
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    // Check if MathJax is already loaded to prevent conflicts
    if (window.MathJax) {
      console.log('MathJax already loaded, skipping initialization');
      return;
    }
    
    // Remove any existing MathJax scripts to avoid conflicts
    const existingConfigScript = document.getElementById('mathjax-config');
    const existingScript = document.getElementById('mathjax-script');
    if (existingConfigScript) existingConfigScript.remove();
    if (existingScript) existingScript.remove();
    
    // Step 1: Set up MathJax configuration before loading the script
    window.MathJax = {
      tex: {
        inlineMath: [['$', '$'], ['\\(', '\\)']],
        displayMath: [['$$', '$$'], ['\\[', '\\]']],
        processEscapes: true,
        processEnvironments: true,
        packages: {'[+]': ['base', 'ams', 'noerrors', 'noundefined', 'autoload']}
      },
      options: {
        enableMenu: false,
        skipHtmlTags: ['script', 'noscript', 'style', 'textarea', 'pre', 'code']
      },
      startup: {
        ready: () => {
          console.log('MathJax is ready');
          if (window.MathJax && window.MathJax.startup) {
            window.MathJax.startup.defaultReady();
          }
        }
      }
    };
    
    // Step 2: Add the main MathJax script
    const script = document.createElement('script');
    script.id = 'mathjax-script';
    script.src = 'https://cdn.jsdelivr.net/npm/mathjax@3.2.2/es5/tex-mml-chtml.js';
    script.async = true;
    script.onload = () => {
      console.log('MathJax script loaded successfully');
    };
    script.onerror = (error) => {
      console.error('Failed to load MathJax script:', error);
    };
    document.head.appendChild(script);
    
    // Return cleanup function
    return () => {
      // Clean up scripts when component unmounts
      const scriptToRemove = document.getElementById('mathjax-script');
      if (scriptToRemove) scriptToRemove.remove();
      // Reset MathJax global
      if (window.MathJax) {
        delete window.MathJax;
      }
    };
  }, []); // Only run once on component mount

  // Step 5: MathJax typesetting removed from global scope - now handled locally in preview component
  
  // Step 4: Clean Quill editor initialization
  useEffect(() => {
    console.log('Step 4: Starting Quill editor initialization');
    
    if (showSource || !editorRef.current) {
      console.log('Step 4: Skipping Quill init - source mode or no editor ref');
      return;
    }
    
    // Clean up any previous instances
    if (quillRef.current) {
      console.log('Step 4: Cleaning up previous Quill instance');
      try {
        if (quillRef.current.root) {
          quillRef.current.root.removeEventListener('paste', handlePaste);
        }
      } catch (err) {
        console.error('Step 4 Error: Failed to cleanup previous instance:', err);
      }
      quillRef.current = null;
      editorRef.current.innerHTML = '';
    }
    
    try {
      console.log('Step 4a: Creating Quill editor container');
      const editorContainer = document.createElement('div');
      editorRef.current.appendChild(editorContainer);
      
      console.log('Step 4b: Initializing Quill with clean configuration');
      const quill = new Quill(editorContainer, {
        theme: 'snow',
        modules: {
          toolbar: false, // Disable default toolbar
          clipboard: {
            matchVisual: false
          },
          keyboard: {
            bindings: {
              // Fix double space issue
              space: {
                key: ' ',
                handler: function(range, context) {
                  const selection = this.quill.getSelection();
                  if (!selection) return true;
                  
                  const text = this.quill.getText(Math.max(0, selection.index - 1), 1);
                  if (text === ' ') {
                    console.log('Step 4b: Preventing double space');
                    return false;
                  }
                  return true;
                }
              }
            }
          }
        },
        placeholder: 'Start writing here...',
      });
      
      console.log('Step 4c: Quill editor created successfully');
      quillRef.current = quill;
      
      // Set initial content if available
      if (htmlValue) {
        try {
          console.log('Step 4d: Setting initial content');
          quill.clipboard.dangerouslyPasteHTML(0, htmlValue);
          quill.setSelection(quill.getLength(), 0);
        } catch (err) {
          console.error('Step 4d Error: Failed to set initial content:', err);
        }
      }
      
      // Handle content changes
      quill.on('text-change', () => {
        try {
          const content = quill.root.innerHTML;
          setValue(content);
          setHtmlValue(content);
        } catch (err) {
          console.error('Step 4 Error: Failed to handle text change:', err);
        }
      });
      
      // Setup paste handler
      try {
        if (quill.root) {
          quill.root.addEventListener('paste', handlePaste);
          console.log('Step 4e: Paste handler setup successfully');
        }
      } catch (err) {
        console.error('Step 4e Error: Failed to setup paste handler:', err);
      }
      
      console.log('Step 4: Quill editor initialization completed successfully');
      
      return () => {
        console.log('Step 4: Cleaning up Quill editor');
        try {
          if (quill && quill.root) {
            quill.root.removeEventListener('paste', handlePaste);
          }
        } catch (err) {
          console.error('Step 4 Error: Failed to cleanup:', err);
        }
      };
    } catch (err) {
      console.error('Step 4 Error: Failed to initialize Quill editor:', err);
      return () => {};
    }
  }, [showSource, htmlValue, handlePaste]);
  
  // Step 6: Simplified UI for testing
  console.log('Step 6: Rendering simplified UI for testing');
  
  return (
    <div className="quill-editor-container p-4">
      <div className="mb-4">
        <h2 className="text-2xl font-bold mb-2">Rich Text Editor - Localized MathJax Integration</h2>
        <div className="text-sm text-gray-600 mb-4">
          <p>MathJax is now handled locally in the preview section below</p>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow">
        {/* Simplified Toolbar */}
        <div className="border-b border-gray-300 p-2">
          <div className="flex items-center space-x-2">
            {/* Basic formatting buttons */}
            <button
              className="p-2 rounded hover:bg-gray-100 transition"
              onClick={() => {
                console.log('Step 6: Bold button clicked');
                if (quillRef.current && !showSource) {
                  quillRef.current.format('bold', !quillRef.current.getFormat().bold);
                }
              }}
              title="Bold"
            >
              <strong>B</strong>
            </button>
            <button
              className="p-2 rounded hover:bg-gray-100 transition"
              onClick={() => {
                console.log('Step 6: Italic button clicked');
                if (quillRef.current && !showSource) {
                  quillRef.current.format('italic', !quillRef.current.getFormat().italic);
                }
              }}
              title="Italic"
            >
              <em>I</em>
            </button>
            
            {/* Additional formatting buttons */}
            <button
              className="p-2 rounded hover:bg-gray-100 transition"
              onClick={() => {
                if (quillRef.current && !showSource) {
                  quillRef.current.format('strike', !quillRef.current.getFormat().strike);
                }
              }}
              title="Strikethrough"
            >
              <s>S</s>
            </button>
            
            {/* Separator */}
            <div className="w-px h-6 bg-gray-300 mx-2"></div>
            
            {/* List buttons */}
            <button
              className="p-2 rounded hover:bg-gray-100 transition"
              onClick={() => {
                if (quillRef.current && !showSource) {
                  quillRef.current.format('list', 'bullet');
                }
              }}
              title="Bullet List"
            >
              •
            </button>
            <button
              className="p-2 rounded hover:bg-gray-100 transition"
              onClick={() => {
                if (quillRef.current && !showSource) {
                  quillRef.current.format('list', 'ordered');
                }
              }}
              title="Numbered List"
            >
              1.
            </button>
            
            {/* Separator */}
            <div className="w-px h-6 bg-gray-300 mx-2"></div>
            
            {/* Header dropdown */}
            <select
              className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 transition"
              onChange={(e) => {
                if (quillRef.current && !showSource) {
                  const value = e.target.value;
                  if (value === 'normal') {
                    quillRef.current.format('header', false);
                  } else {
                    quillRef.current.format('header', parseInt(value));
                  }
                  e.target.value = 'normal'; // Reset dropdown
                }
              }}
              defaultValue="normal"
              title="Headers"
            >
              <option value="normal">Normal</option>
              <option value="1">Heading 1</option>
            </select>
            
            {/* Source code toggle button */}
            <button
              className={`p-2 rounded transition ${
                showSource 
                  ? 'bg-blue-500 text-white' 
                  : 'hover:bg-gray-100'
              }`}
              onClick={() => {
                console.log('Step 6: Source toggle clicked');
                setShowSource(!showSource);
                if (!showSource && quillRef.current) {
                  const html = quillRef.current.root.innerHTML;
                  setHtmlValue(html);
                }
              }}
              title="Toggle Source Code"
            >
              &lt;/&gt;
            </button>
            
            {/* Test MathJax button */}
            <button
              className="p-2 rounded hover:bg-gray-100 transition bg-green-50"
              onClick={() => {
                console.log('Step 6: Testing MathJax rendering');
                if (quillRef.current && !showSource) {
                  const testMath = 'Test equation: $x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}$';
                  quillRef.current.clipboard.dangerouslyPasteHTML(quillRef.current.getLength(), `<p>${testMath}</p>`);
                }
              }}
              title="Insert Test Math"
            >
              ∑
            </button>
          </div>
        </div>
        
        {/* Editor/Source View Container */}
        {showSource ? (
          <textarea
            className="w-full h-[400px] p-4 font-mono text-sm bg-white border-0 rounded-b-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={htmlValue}
            onChange={(e) => {
              console.log('Step 6: Source code changed');
              setHtmlValue(e.target.value);
              setValue(e.target.value);
            }}
            placeholder="HTML source code..."
          />
        ) : (
          <div ref={editorRef} className="h-[400px]" />
        )}
      </div>
      
      {/* Localized MathJax Preview Area */}
      <MathJaxPreview value={value} />
    </div>
  );

  console.log('Step 6: Component render completed');
};

export default QuillEditor;
